import * as React from "react"
import { ChevronUp, ChevronDown } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { useIsMobile } from "@/hooks/use-mobile"

interface TimeInputProps {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  disabled?: boolean
  className?: string
  id?: string
}

export const TimeInput = React.forwardRef<HTMLInputElement, TimeInputProps>(
  ({ value, onChange, min = 0, max = 23, disabled = false, className, id, ...props }, ref) => {
    const isMobile = useIsMobile()
    
    const increment = () => {
      if (disabled) return
      const newValue = value >= max ? min : value + 1
      onChange(newValue)
    }
    
    const decrement = () => {
      if (disabled) return
      const newValue = value <= min ? max : value - 1
      onChange(newValue)
    }
    
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return
      const val = parseInt(e.target.value)
      if (!isNaN(val) && val >= min && val <= max) {
        onChange(val)
      }
    }

    if (isMobile) {
      return (
        <div className={cn("flex items-center gap-1", className)}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={decrement}
            disabled={disabled}
            className="h-9 w-9 p-0 bg-component-background text-foreground border border-foreground shrink-0"
            aria-label="Decrease time"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
          <Input
            id={id}
            ref={ref}
            type="number"
            min={min}
            max={max}
            value={value}
            onChange={handleInputChange}
            disabled={disabled}
            className="h-9 text-center text-sm font-medium min-w-0 flex-1 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0 [&::-webkit-outer-spin-button]:m-0"
            style={{ MozAppearance: 'textfield' }}
            {...props}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={increment}
            disabled={disabled}
            className="h-9 w-9 p-0 bg-component-background text-foreground border border-foreground shrink-0"
            aria-label="Increase time"
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
        </div>
      )
    }

    // Desktop version - traditional number input with smaller arrow buttons on the side
    return (
      <div className={cn("flex items-center gap-1", className)}>
        <Input
          id={id}
          ref={ref}
          type="number"
          min={min}
          max={max}
          value={value}
          onChange={handleInputChange}
          disabled={disabled}
          className="h-7 text-xs flex-1 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0 [&::-webkit-outer-spin-button]:m-0"
          style={{ MozAppearance: 'textfield' }}
          {...props}
        />
        <div className="flex flex-col gap-0.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={increment}
            disabled={disabled}
            className="h-3 w-6 p-0 bg-component-background text-foreground border border-foreground"
            aria-label="Increase time"
          >
            <ChevronUp className="h-2 w-2" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={decrement}
            disabled={disabled}
            className="h-3 w-6 p-0 bg-component-background text-foreground border border-foreground"
            aria-label="Decrease time"
          >
            <ChevronDown className="h-2 w-2" />
          </Button>
        </div>
      </div>
    )
  }
)

TimeInput.displayName = "TimeInput"